import React, { useState } from 'react';
import { useSettings } from '../context/SettingsContext';
import { useGoals } from '../context/GoalsContext';

function Goals() {
  const { formatCurrency } = useSettings();
  const { goals, addGoal, updateGoal } = useGoals();

  const [newGoal, setNewGoal] = useState({ name: '', target: '', category: 'savings' });
  const [showAddGoal, setShowAddGoal] = useState(false);

  const handleAddGoal = async () => {
    if (newGoal.name && newGoal.target) {
      console.log('Adding goal:', newGoal);
      const result = await addGoal({
        name: newGoal.name,
        target: parseFloat(newGoal.target),
        category: newGoal.category,
        description: `${newGoal.category} goal`,
        deadline: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      });
      console.log('Goal add result:', result);
      setNewGoal({ name: '', target: '', category: 'savings' });
      setShowAddGoal(false);
    }
  };

  const updateGoalProgress = async (id, amount) => {
    const goal = goals.find(g => g.id === id);
    if (!goal) return;

    const newAmount = Math.max(0, (goal.currentAmount || 0) + amount);
    const progress = Math.round((newAmount / goal.target) * 100);

    await updateGoal(id, { currentAmount: newAmount, progress });
  };



  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>Savings Goals</h1>
          <button
            onClick={() => setShowAddGoal(true)}
            className="btn btn-primary"
          >
            Add New Goal
          </button>
        </div>
        <p style={{ color: 'var(--gray-500)' }}>Track your financial goals and savings progress</p>
      </div>

      {showAddGoal && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Add New Goal</h3>
          <div className="grid-form-4">
            <div>
              <label className="form-label">Goal Name</label>
              <input
                type="text"
                className="form-input"
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                placeholder="e.g., Emergency Fund"
              />
            </div>
            <div>
              <label className="form-label">Target Amount</label>
              <input
                type="number"
                className="form-input"
                value={newGoal.target}
                onChange={(e) => setNewGoal({ ...newGoal, target: e.target.value })}
                placeholder="10000"
              />
            </div>
            <div>
              <label className="form-label">Category</label>
              <select
                className="form-input"
                value={newGoal.category}
                onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}
              >
                <option value="savings">Savings</option>
                <option value="purchase">Purchase</option>
                <option value="investment">Investment</option>
                <option value="debt">Debt Payoff</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleAddGoal} className="btn btn-primary">Add</button>
              <button onClick={() => setShowAddGoal(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="auto-grid-min350">
        {goals.map(goal => {
          const progress = goal.progress || 0;
          const current = goal.currentAmount || 0;
          const remaining = goal.target - current;

          return (
            <div key={goal.id} className="card">
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>{goal.name}</h3>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    background: 'var(--gray-100)',
                    color: 'var(--gray-600)',
                    border: '1px solid var(--gray-200)'
                  }}>
                    {goal.category}
                  </span>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Progress</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{progress.toFixed(1)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: 'var(--gray-200)',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${Math.min(progress, 100)}%`,
                      height: '100%',
                      background: 'var(--gray-600)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Current</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--gray-700)' }}>
                      {formatCurrency(current)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Target</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                      {formatCurrency(goal.target)}
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: '0.75rem',
                  background: 'var(--gray-100)',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                    {remaining > 0 ? `${formatCurrency(remaining)} remaining` : 'Goal achieved!'}
                  </div>
                </div>

                {progress < 100 && (
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                      <button
                        onClick={() => updateGoalProgress(goal.id, 100)}
                        className="btn btn-primary"
                        style={{ flex: 1, fontSize: '0.875rem' }}
                      >
                        +$100
                      </button>
                      <button
                        onClick={() => updateGoalProgress(goal.id, 500)}
                        className="btn btn-primary"
                        style={{ flex: 1, fontSize: '0.875rem' }}
                      >
                        +$500
                      </button>
                      <button
                        onClick={() => updateGoalProgress(goal.id, -100)}
                        className="btn btn-secondary"
                        style={{ flex: 1, fontSize: '0.875rem' }}
                      >
                        -$100
                      </button>
                    </div>

                    {/* Manual Input */}
                    <div>
                      <label style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--gray-600)',
                        marginBottom: '0.5rem',
                        display: 'block'
                      }}>
                        Add Custom Amount
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="number"
                          placeholder="Enter custom amount"
                          className="form-input"
                          style={{
                            flex: 1,
                            fontSize: '0.875rem'
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              const amount = parseFloat(e.target.value);
                              if (!isNaN(amount)) {
                                updateGoalProgress(goal.id, amount);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          onClick={(e) => {
                            const input = e.target.previousElementSibling;
                            const amount = parseFloat(input.value);
                            if (!isNaN(amount)) {
                              updateGoalProgress(goal.id, amount);
                              input.value = '';
                            }
                          }}
                          className="btn btn-primary"
                          style={{ fontSize: '0.875rem' }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Goals;